import { LEVELS, type Level, TIERS, tierOf } from '@xox/shared'
import { useState } from 'react'
import { BottomSheet } from '../components/BottomSheet'
import { LevelCard, TierHeader } from './LevelCard'
import { loadProgress } from './progress'

interface Props {
  open: boolean
  value: string
  onPick: (level: Level) => void
  onClose: () => void
}

/** Every level is pickable in a race; solo progress only decorates the cards with stars. */
export function LevelPickerSheet({ open, value, onPick, onClose }: Props) {
  const currentTier = tierOf(LEVELS.findIndex((l) => l.id === value) + 1).id
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})
  const progress = loadProgress()
  const isOpen = (tierId: number) => expanded[tierId] ?? tierId === currentTier

  return (
    <BottomSheet open={open} onClose={onClose} title="Choose a level" wide>
      <div className="flex flex-col gap-2">
        {TIERS.map((tier) => {
          const levels = LEVELS.slice(tier.from - 1, tier.to)
          const isExpanded = isOpen(tier.id)
          return (
            <section key={tier.id} aria-labelledby={`tier-${tier.id}`}>
              <TierHeader
                tier={tier}
                expanded={isExpanded}
                onToggle={() => setExpanded((o) => ({ ...o, [tier.id]: !isExpanded }))}
                meta={`#${tier.from}–${tier.to}`}
              />
              {isExpanded && (
                <ul className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {levels.map((level) => (
                    <LevelCard
                      key={level.id}
                      level={level}
                      number={LEVELS.indexOf(level) + 1}
                      unlocked
                      done={progress[level.id]}
                      selected={level.id === value}
                      onPick={(l) => {
                        onPick(l)
                        onClose()
                      }}
                    />
                  ))}
                </ul>
              )}
            </section>
          )
        })}
      </div>
    </BottomSheet>
  )
}
